import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, map, switchMap } from 'rxjs';

import { API_URL } from '../config/api.config';
import {
  ApiResponse,
  Banco,
  BancoIntegracion,
  BancoIntegracionRequest,
  BancoRequest,
} from '../models';
import {
  PagedResponse,
  PaginationRequest,
} from '../../shared/data-table-paginado/data-table-paginado.models';

@Injectable({ providedIn: 'root' })
export class BancosService {
  private readonly http = inject(HttpClient);
  private readonly resourceUrl = `${API_URL}/bancos`;

  getAll(): Observable<Banco[]> {
    return this.http
      .get<ApiResponse<Banco[]>>(this.resourceUrl)
      .pipe(map((response) => response.data ?? []));
  }

  getPage(request: PaginationRequest): Observable<ApiResponse<PagedResponse<Banco>>> {
    let params = new HttpParams().set('page', request.page).set('pageSize', request.pageSize);

    if (request.search?.trim()) params = params.set('search', request.search.trim());
    if (request.status && request.status !== 'Todos') params = params.set('status', request.status);

    return this.http.get<ApiResponse<PagedResponse<Banco>>>(`${this.resourceUrl}/paginado`, {
      params,
    });
  }

  getIntegraciones(bancoId: string): Observable<BancoIntegracion[]> {
    return this.http
      .get<ApiResponse<BancoIntegracion[]>>(`${this.resourceUrl}/${bancoId}/integraciones`)
      .pipe(map((response) => response.data ?? []));
  }

  createBank(request: BancoRequest): Observable<Banco> {
    return this.http
      .post<ApiResponse<Banco>>(this.resourceUrl, request)
      .pipe(map((response) => this.requireData(response)));
  }

  updateBank(id: string, request: BancoRequest): Observable<Banco> {
    return this.http
      .put<ApiResponse<Banco>>(`${this.resourceUrl}/${id}`, request)
      .pipe(map((response) => this.requireData(response)));
  }

  toggleBank(id: string, estaActivo: boolean): Observable<Banco> {
    return this.http
      .patch<ApiResponse<Banco>>(`${this.resourceUrl}/${id}/estado`, { estaActivo })
      .pipe(map((response) => this.requireData(response)));
  }

  getAllIntegraciones(): Observable<BancoIntegracion[]> {
    return this.getAll().pipe(
      switchMap((banks) => forkJoin(banks.map((bank) => this.getIntegraciones(bank.id)))),
      map((groups) => groups.flat()),
    );
  }

  createIntegration(
    bancoId: string,
    request: BancoIntegracionRequest,
  ): Observable<BancoIntegracion> {
    return this.http
      .post<ApiResponse<BancoIntegracion>>(`${this.resourceUrl}/${bancoId}/integraciones`, request)
      .pipe(map((response) => this.requireData(response)));
  }

  updateIntegration(
    bancoId: string,
    integrationId: string,
    request: BancoIntegracionRequest,
  ): Observable<BancoIntegracion> {
    return this.http
      .put<ApiResponse<BancoIntegracion>>(
        `${this.resourceUrl}/${bancoId}/integraciones/${integrationId}`,
        request,
      )
      .pipe(map((response) => this.requireData(response)));
  }

  toggleIntegration(
    bancoId: string,
    integrationId: string,
    estaActivo: boolean,
  ): Observable<BancoIntegracion> {
    return this.http
      .patch<ApiResponse<BancoIntegracion>>(
        `${this.resourceUrl}/${bancoId}/integraciones/${integrationId}/estado`,
        { estaActivo },
      )
      .pipe(map((response) => this.requireData(response)));
  }

  testIntegration(bancoId: string, integrationId: string): Observable<unknown> {
    return this.http
      .post<ApiResponse<unknown>>(
        `${this.resourceUrl}/${bancoId}/integraciones/${integrationId}/probar`,
        {},
      )
      .pipe(map((response) => this.requireData(response)));
  }

  private requireData<T>(response: ApiResponse<T>): T {
    if (response.data === null) throw new Error(response.message ?? 'La API no devolvió datos.');
    return response.data;
  }
}
